import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash } from "@phosphor-icons/react";

export default function SuperStores() {
  const nav = useNavigate();
  const [stores, setStores] = useState([]);
  const [deleting, setDeleting] = useState(null); // store object pending deletion
  const [confirmText, setConfirmText] = useState("");
  const [working, setWorking] = useState(false);

  const load = async () => setStores((await api.get("/admin/stores")).data);
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/stores/${id}/status`, null, { params: { status } });
      toast.success(`Store ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const manageAs = (store) => {
    sessionStorage.setItem("qd_impersonate_store", store.id);
    nav("/admin");
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    if (confirmText.trim() !== deleting.name) {
      toast.error("Type the exact store name to confirm.");
      return;
    }
    setWorking(true);
    try {
      const r = await api.delete(`/admin/stores/${deleting.id}`);
      const d = r.data?.deleted || {};
      toast.success(
        `Deleted "${deleting.name}" — ${d.products || 0} products, ${d.orders || 0} orders removed. ` +
        `Owner's mobile is NOT blocked.`
      );
      setDeleting(null);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete store");
    }
    setWorking(false);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">All stores</h1>
        <p className="text-gray-600 text-sm">{stores.length} stores</p>
      </div>

      <div className="qd-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="p-3 font-semibold">Store</th>
              <th className="p-3 font-semibold">URL</th>
              <th className="p-3 font-semibold">Products</th>
              <th className="p-3 font-semibold">Orders</th>
              <th className="p-3 font-semibold">Plan</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold">Availability</th>
              <th className="p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((s) => (
              <tr key={s.id} className="border-b border-gray-50" data-testid={`super-store-${s.slug}`}>
                <td className="p-3">
                  <button
                    onClick={() => manageAs(s)}
                    className="text-left font-semibold text-black hover:text-[#F8CB46] hover:underline decoration-2 underline-offset-2 transition-colors"
                    data-testid={`manage-store-${s.slug}`}
                    title="Open this seller's dashboard as Super Admin">
                    {s.name}
                  </button>
                  <div className="text-xs text-gray-500">{s.city}</div>
                </td>
                <td className="p-3 font-mono text-xs">/{s.slug}</td>
                <td className="p-3">{s.product_count}</td>
                <td className="p-3">{s.order_count}</td>
                <td className="p-3"><span className="text-xs px-2 py-1 rounded-full bg-[#F8CB46]/20 text-black font-bold capitalize">{s.subscription_plan}</span></td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-bold capitalize ${
                    s.status === "approved" ? "bg-green-50 text-green-700" :
                    s.status === "suspended" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                  }`}>{s.status}</span>
                </td>
                <td className="p-3" data-testid={`super-availability-${s.slug}`}>
                  {s.availability_status?.is_open ? (
                    <div>
                      <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 font-bold">Open</span>
                      <div className="text-[10px] text-gray-500 mt-1 font-mono">{s.availability_status.open_time}–{s.availability_status.close_time}</div>
                    </div>
                  ) : (
                    <div>
                      <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 font-bold">Closed</span>
                      <div className="text-[10px] text-gray-500 mt-1 max-w-[140px] truncate" title={s.availability_status?.reason || ""}>{s.availability_status?.reason || "—"}</div>
                    </div>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {s.status !== "approved" && (
                      <button onClick={() => setStatus(s.id, "approved")}
                        className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-bold"
                        data-testid={`approve-${s.slug}`}>Approve</button>
                    )}
                    {s.status !== "suspended" && (
                      <button onClick={() => setStatus(s.id, "suspended")}
                        className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-bold"
                        data-testid={`suspend-${s.slug}`}>Suspend</button>
                    )}
                    <button
                      onClick={() => { setDeleting(s); setConfirmText(""); }}
                      className="text-xs px-2 py-1 rounded bg-red-600 text-white font-bold inline-flex items-center gap-1"
                      data-testid={`delete-${s.slug}`}>
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Strong delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(open) => { if (!open) { setDeleting(null); setConfirmText(""); } }}>
        <AlertDialogContent data-testid="delete-store-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Permanently delete store?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will <strong>permanently delete</strong> "<strong>{deleting?.name}</strong>" and ALL its data:
              </span>
              <span className="block text-sm">
                • {deleting?.product_count || 0} products • {deleting?.order_count || 0} orders • categories • pincodes
              </span>
              <span className="block text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5 mt-2">
                The shopkeeper's mobile number will <strong>NOT</strong> be blocked. They can sign up again later and create a new store.
              </span>
              <span className="block text-sm font-bold mt-2">This action cannot be undone.</span>
              <span className="block text-xs mt-3 text-gray-600">
                To confirm, type the store name below: <span className="font-mono font-bold">{deleting?.name}</span>
              </span>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="qd-input mt-1 w-full"
                placeholder="Type store name to confirm"
                data-testid="delete-store-confirm-input"
                autoFocus
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-store-cancel" disabled={working}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={working || confirmText.trim() !== (deleting?.name || "")}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
              data-testid="delete-store-confirm">
              {working ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
